import React, { useContext, useEffect, useRef, useState } from "react";
import { AiOutlineExperiment, AiOutlineSend } from "react-icons/ai";
import uuid from "react-uuid";
import FetchContext from "../../../contexts/fetch";
import FlowContext from "../../../contexts/flowContext";
import UserContext from "../../../contexts/user";
import { flowToConfig } from "../../Home/Controls/utils";
import AudioPlayer from "./AudioPlayer";
import styles from "./callBot.module.css";
import CallbotBackendError from "./Errors/CallbotBackendError";
import IvasError from "./Errors/IvasError";
import Latency from "./Latency";
import useIvasSimulation from "./hooks/useIvasSimulation";
import useIvasV2Simulation from "./hooks/useIvasV2Simulation";
import Logs from "./Logs";

const CallBot = ({ fetchApi, setLoading }) => {
  const [fetchState, fetchDispatcher] = useContext(FetchContext);
  const [userState] = useContext(UserContext);
  const [flowState] = useContext(FlowContext);

  const bottomRef = useRef(null);

  const [isMounted, setIsMounted] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [userCanRespond, setUserCanRespond] = useState(false);
  const [userResponse, setUserResponse] = useState("");
  const [ivasResponse, setIvasResponse] = useState("");
  const [ivasErrors, setIvasErrors] = useState([]);
  const [callbotLogsDisplayed, setCallbotLogsDisplayed] = useState(false);

  const [ready, setReady] = useState(true);
  const [callbot_api, setCallbot_api] = useState(
    process.env.REACT_APP_MACI_BACKEND_API_ENDPOINT
  );
  const [callSid, setCallSid] = useState(
    `${userState.name}_simulation_${uuid()}`
  );

  const fetchStateRef = useRef(fetchState);
  const ivasResponseRef = useRef(ivasResponse);

  useEffect(() => {
    fetchStateRef.current = fetchState;
  }, [fetchState]);

  useEffect(() => {
    ivasResponseRef.current = ivasResponse;
  }, [ivasResponse]);

  const { askIvas, status, connectToIvasServer, interrupt, skip } =
    useIvasSimulation(
      callSid,
      (text) => {
        setConversation((prevConversation) => [
          ...prevConversation,
          {
            author: "call-bot",
            text,
            fromIvas: true,
          },
        ]);
        setIvasResponse(
          (previousIvasResponse) => `${previousIvasResponse} ${text}`
        );
      },
      (errors) => {
        setIvasErrors(errors);
      },
      () => {
        simulate({
          ...fetchStateRef.current.callBotSimulationResult?.nextUrlParameters,
          ivasResponse: ivasResponseRef.current,
        });
      }
    );
  const {
    askIvasV2,
    ivasV2Status,
    connectToIvasV2,
    sendToIvasV2,
    interruptIvasV2,
    skipV2,
  } = useIvasV2Simulation(
    callSid,
    (text) => {
      setConversation((prevConversation) => [
        ...prevConversation,
        {
          author: "call-bot",
          text,
          fromIvas: true,
        },
      ]);
      setIvasResponse(
        (previousIvasResponse) => `${previousIvasResponse} ${text}`
      );
      setLoading(false);
    },
    setUserCanRespond,
    (errors) => {
      setIvasErrors(errors);
    },
    () => {
      setLoading(true);
    },
    () => {
      simulate({
        ...fetchStateRef.current.callBotSimulationResult?.nextUrlParameters,
        ivasResponse: ivasResponseRef.current,
      });
    }
  );

  const simulate = function (additionalParameters = {}) {
    fetchApi(
      callbot_api,
      {
        ...additionalParameters,
        callSid: callSid,
        from: userState.phoneNumber,
        name: flowState.name,
      },
      flowToConfig(
        flowState,
        userState.currentApp,
        fetchState.clientConfig.edge
      )
    );
  };

  // fetch simulation api
  useEffect(() => {
    setIsMounted(true);

    if (ready && callbot_api) {
      simulate();
      console.log(
        "flow to config: ",
        flowToConfig(
          flowState,
          userState.currentApp,
          fetchState.clientConfig.edge
        )
      );
    }
  }, [ready, callbot_api]);

  // auto scroll to bottom of conversation
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  useEffect(() => {
    setLoading(fetchState.loadingSimulateFlowApi);
  }, [fetchState.loadingSimulateFlowApi]);

  // update conversation when fetching is done
  useEffect(() => {
    if (fetchState.callBotSimulationResult && isMounted) {
      fetchDispatcher({
        type: "fetch-callbot-simulation-logs",
        queryParameters: {
          callSid,
        },
      });

      setConversation([
        ...conversation,
        {
          author: "call-bot",
          text: fetchState.callBotSimulationResult.text,
        },
      ]);

      setUserCanRespond(
        fetchState.callBotSimulationResult.others.userShouldRespond
      );

      if (
        !fetchState.callBotSimulationResult.others.userShouldRespond &&
        fetchState.callBotSimulationResult.text.length &&
        !fetchState.callBotSimulationResult.hangup &&
        !fetchState.callBotSimulationResult?.error &&
        !fetchState.callBotSimulationResult.others.connectToIvasV2 &&
        !fetchState.callBotSimulationResult.others.dial
      ) {
        simulate(fetchState.callBotSimulationResult.nextUrlParameters);
      }
      if (fetchState.callBotSimulationResult.others.connectToIvasServer) {
        connectToIvasServer();
      } else if (fetchState.callBotSimulationResult.others.connectToIvasV2) {
        connectToIvasV2();
        setUserCanRespond(true);
      }
    }
  }, [fetchState.callBotSimulationResult]);

  useEffect(() => {
    if (status === 1) {
      askIvas(fetchState.callBotSimulationResult.others);
    }
  }, [status]);

  useEffect(() => {
    if (ivasV2Status === 1 && fetchState.callBotSimulationResult?.others) {
      askIvasV2(fetchState.callBotSimulationResult.others);
    }
  }, [ivasV2Status, fetchState.callBotSimulationResult?.others]);

  const handleResponseBoxChange = function (e) {
    setUserResponse(e.target.value);
  };

  const handleResponseButtonClick = function () {
    if (!fetchState.loadingSimulateFlowApi) {
      setConversation([
        ...conversation,
        {
          author: "user",
          text: userResponse,
        },
      ]);

      if (ivasV2Status === 1) {
        sendToIvasV2(userResponse);
      } else {
        simulate({
          ...fetchState.callBotSimulationResult.nextUrlParameters,
          is_post: 1,
          userResponse,
        });
      }

      setUserResponse("");
    }
  };

  const handleRestartButtonClick = function () {
    setConversation([]);
    const newCallSid = `${userState.name}_simulation_${uuid()}`;
    setCallSid(newCallSid);

    simulate();
  };

  const formatLine = function (line, speechText, index) {
    const urlRegex = /--(.*?)--/;
    const urlMatches = line.match(urlRegex);

    const latencyRegex = /\*\*(.*?)\*\*/;
    const latencyMatches = line.match(latencyRegex);

    const lineRegex = /^([^*]*)\*\*([^*]*)\*\*([^*]*)$/;
    const lineMatches = line.match(lineRegex);

    if (urlMatches) {
      const audioUrl = urlMatches[1];

      return (
        <div>
          {latencyMatches ? (
            <div className={styles.latency_container}>
              <Latency latency={latencyMatches[1]}>
                <AudioPlayer audioUrl={audioUrl} />
              </Latency>
            </div>
          ) : (
            <AudioPlayer audioUrl={audioUrl} />
          )}
        </div>
      );
    } else {
      return (
        <div
          key={index}
          className={
            speechText.author === "call-bot"
              ? speechText.fromIvas
                ? styles.ivas_line
                : styles.call_bot_line
              : styles.user_line
          }
        >
          {latencyMatches ? (
            <div className={styles.latency_container}>
              <Latency latency={latencyMatches[1]}>
                {lineMatches ? lineMatches[1] : line}
              </Latency>
            </div>
          ) : (
            <div>{line}</div>
          )}
        </div>
      );
    }
  };

  const collapseLogs = function () {
    setCallbotLogsDisplayed(!callbotLogsDisplayed);
  };

  return (
    <div>
      {/* {fetchState.simulationError && JSON.stringify(fetchState.simulationError)} */}
      <div className={styles.logs_container}>
        <Logs
          callSid={callSid}
          collapse={collapseLogs}
          collapsed={callbotLogsDisplayed}
        />
      </div>
      <div
        className={styles.conversation_container}
        style={{ top: callbotLogsDisplayed ? "500px" : "70px" }}
      >
        {fetchState.callBotSimulationResult?.error && (
          <CallbotBackendError
            errors={fetchState.callBotSimulationResult.error}
          />
        )}

        {ivasErrors.length > 0 && <IvasError errors={ivasErrors} />}

        {conversation.map((speechText, index) => (
          <div
            key={index}
            className={
              speechText.author === "user" ? styles.user_line_container : ""
            }
          >
            {speechText.text
              .split("\n")
              .filter((line) => line.length)
              .map((line, index) => formatLine(line, speechText, index))}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      {userCanRespond && (
        <div className={styles.respond_box_container}>
          <textarea
            className={styles.respond_box}
            value={userResponse}
            onChange={handleResponseBoxChange}
            onKeyDown={(e) => {
              if (e.keyCode == 13 && e.shiftKey == false) {
                e.preventDefault();
                handleResponseButtonClick();
              }
            }}
          />
          <div className={styles.send_button_container}>
            <AiOutlineSend
              className={styles.send_button}
              onClick={handleResponseButtonClick}
            />
          </div>
        </div>
      )}
      {(fetchState.callBotSimulationResult &&
        fetchState.callBotSimulationResult.hangup) ||
        (fetchState.callBotSimulationResult?.others?.dial && (
          <div className={styles.restart_box_container}>
            <div>
              <h2>Simulation terminée</h2>
            </div>
            <div className={styles.restart_button_container}>
              <AiOutlineExperiment
                className={styles.restart_button}
                onClick={handleRestartButtonClick}
              />
              <span>Recommencer</span>
            </div>
          </div>
        ))}
      <div className={styles.ivas_status_container}>
        <span>IVAS/V2 CONNECTION STATUS</span>
        <span
          className={`
          ${styles.ivas_status} 
          ${
            status === 1 || ivasV2Status === 1
              ? styles.ivas_status_on
              : styles.ivas_status_off
          }`}
        >
          &#9679;
        </span>

        <div>
          {(status === 1 || ivasV2Status === 1) && (
            <span
              className={`${styles.ivas_icon} ${styles.ivas_stop_icon}`}
              onClick={status === 1 ? interrupt : interruptIvasV2}
            >
              ⏹
            </span>
          )}
          <span
            className={`${styles.ivas_icon} ${styles.ivas_skip_icon}`}
            onClick={status === 1 ? skip : skipV2}
          >
            ⏩
          </span>
        </div>
      </div>
    </div>
  );
};

export default CallBot;
